import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import type { ContentResponse, ContentEditEntry } from '../../types';
import { FaqGeneratorSection } from '../../components/dashboard/content/FaqGeneratorSection';

interface Props {
  domainId: string;
  domain: string;
  faqGeneratorPrefillUrl?: string;
}

// Maps field_path → friendly label + input type
const SEO_FIELDS: { path: string; label: string; maxHint: string; multiline?: boolean }[] = [
  { path: 'schema.pageSignals.pageTitle',    label: 'Meta title',        maxHint: '50–60 chars' },
  { path: 'schema.pageSignals.metaDesc',     label: 'Meta description',  maxHint: '150–160 chars', multiline: true },
  { path: 'schema.pageSignals.ogTitle',      label: 'OG title',          maxHint: '50–60 chars' },
  { path: 'schema.pageSignals.ogDescription',label: 'OG description',    maxHint: '150–160 chars', multiline: true },
];

const GEO_FIELDS: { path: string; label: string; multiline?: boolean }[] = [
  { path: 'schema.businessName',            label: 'Business name' },
  { path: 'schema.category',               label: 'Category' },
  { path: 'schema.description',            label: 'How AI will describe you', multiline: true },
  { path: 'schema.address.streetAddress',  label: 'Street address' },
  { path: 'schema.address.addressLocality',label: 'City' },
  { path: 'schema.address.postalCode',     label: 'Postcode' },
  { path: 'schema.address.addressCountry', label: 'Country' },
  { path: 'schema.telephone',              label: 'Phone' },
];

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, key) => {
    if (cur !== null && typeof cur === 'object') {
      return (cur as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

interface FieldRowProps {
  path: string;
  label: string;
  maxHint?: string;
  multiline: boolean | undefined;
  aiValue: string;
  editEntry: ContentEditEntry | undefined;
  onSave: (path: string, value: string, aiValue: string) => Promise<void>;
}

function FieldRow({ path, label, maxHint, multiline, aiValue, editEntry, onSave }: FieldRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const currentValue = editEntry?.human_value ?? aiValue;
  const isEdited = !!editEntry?.human_value;

  function startEdit() {
    setDraft(currentValue);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    try {
      await onSave(path, draft, aiValue);
      setEditing(false);
      setToast('Saved — live within 60 seconds');
      setTimeout(() => setToast(''), 3000);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-brand-ink">{label}</span>
          {maxHint && <span className="text-xs text-gray-400">({maxHint})</span>}
          {isEdited
            ? <span className="text-xs bg-green-50 text-brand-green px-2 py-0.5 rounded-full">Edited</span>
            : <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">AI generated</span>
          }
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            className="text-xs text-gray-400 hover:text-brand-ink flex items-center gap-1 transition-colors"
          >
            ✏️ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div>
          {multiline ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-brand-green"
            />
          ) : (
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-brand-green"
            />
          )}
          {isEdited && (
            <p className="text-xs text-gray-400 italic mt-1">AI original: {aiValue || '—'}</p>
          )}
          <div className="flex gap-2 mt-2">
            <button
              onClick={save}
              disabled={saving}
              className="text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-xs text-gray-500 px-3 py-1.5 rounded-lg hover:text-brand-ink transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-700 leading-relaxed">{currentValue || <span className="text-gray-400 italic">Not set</span>}</p>
      )}

      {toast && (
        <p className="text-xs text-brand-green mt-1">{toast}</p>
      )}
    </div>
  );
}

export function ContentTab({ domainId, domain, faqGeneratorPrefillUrl }: Props) {
  const [content, setContent] = useState<ContentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.domains.getContent(domainId)
      .then(setContent)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load content'))
      .finally(() => setLoading(false));
  }, [domainId]);

  const handleSave = useCallback(async (path: string, value: string, aiVal: string) => {
    await api.domains.saveContent(domainId, path, value, aiVal);
    setContent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        edits: {
          ...prev.edits,
          [path]: { ai_value: aiVal, human_value: value },
        },
      };
    });
  }, [domainId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-500">{error}</p>;
  }

  if (!content || Object.keys(content.payload).length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-8 text-center space-y-2">
        <p className="text-gray-500 text-sm font-medium">Content not generated yet</p>
        <p className="text-xs text-gray-400 max-w-sm mx-auto leading-relaxed">
          AI content is extracted after each audit. If you have already run audits, click{' '}
          <strong className="text-gray-500">Reaudit</strong> on the Overview tab to regenerate —
          content will appear here within a minute.
        </p>
      </div>
    );
  }

  const payload = content.payload;

  function getAiValue(path: string): string {
    const val = getNestedValue(payload, path);
    return val != null ? String(val) : '';
  }

  return (
    <div className="space-y-6">
      {/* SEO fields */}
      <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 className="font-semibold text-brand-ink mb-4">SEO fields</h2>
        <div>
          {SEO_FIELDS.map((f) => (
            <FieldRow
              key={f.path}
              path={f.path}
              label={f.label}
              maxHint={f.maxHint}
              multiline={f.multiline}
              aiValue={getAiValue(f.path)}
              editEntry={content.edits[f.path]}
              onSave={handleSave}
            />
          ))}
        </div>
      </section>

      {/* GEO / schema fields */}
      <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h2 className="font-semibold text-brand-ink mb-1">GEO &amp; schema fields</h2>
        <p className="text-xs text-gray-400 mb-4">
          These values are used to build your schema graph and are read by AI platforms to understand your business.
        </p>
        <div>
          {GEO_FIELDS.map((f) => (
            <FieldRow
              key={f.path}
              path={f.path}
              label={f.label}
              multiline={f.multiline}
              aiValue={getAiValue(f.path)}
              editEntry={content.edits[f.path]}
              onSave={handleSave}
            />
          ))}
        </div>
      </section>

      {/* sameAs URLs */}
      <SameAsSection
        domainId={domainId}
        payload={payload}
        edits={content.edits}
        onSave={handleSave}
      />

      {/* FAQ pairs */}
      <FaqSection
        domainId={domainId}
        payload={payload}
        edits={content.edits}
        onSave={handleSave}
      />

      {/* FAQ Generator — Sprint 2.7.3.2 */}
      <FaqGeneratorSection
        domainId={domainId}
        domain={domain}
        {...(faqGeneratorPrefillUrl !== undefined ? { prefillUrl: faqGeneratorPrefillUrl } : {})}
      />
    </div>
  );
}

// ── sameAs URLs ───────────────────────────────────────────────────────────────

function SameAsSection({
  domainId: _domainId,
  payload,
  edits: _edits,
  onSave,
}: {
  domainId: string;
  payload: Record<string, unknown>;
  edits: Record<string, ContentEditEntry>;
  onSave: (path: string, value: string, aiVal: string) => Promise<void>;
}) {
  const rawUrls = (payload['schema'] as Record<string, unknown> | undefined)?.['sameAsUrls'];
  const aiUrls: string[] = Array.isArray(rawUrls) ? rawUrls.map(String) : [];
  const [urls, setUrls] = useState<string[]>(aiUrls);
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  async function save(next: string[]) {
    setSaving(true);
    try {
      await onSave('schema.sameAsUrls', JSON.stringify(next), JSON.stringify(aiUrls));
      setToast('Saved');
      setTimeout(() => setToast(''), 2000);
    } catch {
      setToast('Save failed');
    } finally {
      setSaving(false);
    }
  }

  function add() {
    const trimmed = newUrl.trim();
    if (!trimmed || urls.includes(trimmed)) return;
    const next = [...urls, trimmed];
    setUrls(next);
    setNewUrl('');
    void save(next);
  }

  function remove(url: string) {
    const next = urls.filter((u) => u !== url);
    setUrls(next);
    void save(next);
  }

  return (
    <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
      <h2 className="font-semibold text-brand-ink mb-1">sameAs URLs</h2>
      <p className="text-xs text-gray-400 mb-4">
        Add your Google Business Profile, Yelp, TripAdvisor, LinkedIn, Facebook — these tell AI platforms where to find verified information about you.
      </p>
      <div className="space-y-2 mb-3">
        {urls.map((url) => (
          <div key={url} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-sm text-brand-ink break-all">{url}</span>
            <button onClick={() => remove(url)} className="ml-2 text-gray-400 hover:text-red-500 text-xs shrink-0">✕</button>
          </div>
        ))}
        {urls.length === 0 && <p className="text-sm text-gray-400 italic">No URLs added yet</p>}
      </div>
      <div className="flex gap-2">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="https://..."
          className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green"
        />
        <button
          onClick={add}
          disabled={saving}
          className="text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-50 transition-colors"
        >
          Add
        </button>
      </div>
      {toast && <p className="text-xs text-brand-green mt-2">{toast}</p>}
    </section>
  );
}

// ── FAQ pairs ─────────────────────────────────────────────────────────────────

interface FaqPair { question: string; answer: string }

function FaqSection({
  domainId: _domainId,
  payload,
  edits: _edits,
  onSave,
}: {
  domainId: string;
  payload: Record<string, unknown>;
  edits: Record<string, ContentEditEntry>;
  onSave: (path: string, value: string, aiVal: string) => Promise<void>;
}) {
  const rawFaqs = payload['faqPairs'];
  const aiFaqs: FaqPair[] = Array.isArray(rawFaqs)
    ? (rawFaqs as FaqPair[]).filter((f) => f.question)
    : [];
  const [faqs, setFaqs] = useState<FaqPair[]>(aiFaqs);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  async function save(next: FaqPair[]) {
    setSaving(true);
    try {
      await onSave('faqPairs', JSON.stringify(next), JSON.stringify(aiFaqs));
      setToast('Saved');
      setTimeout(() => setToast(''), 2000);
    } catch {
      setToast('Save failed');
    } finally {
      setSaving(false);
    }
  }

  function update(i: number, field: 'question' | 'answer', val: string) {
    const next = faqs.map((f, idx) => idx === i ? { ...f, [field]: val } : f);
    setFaqs(next);
  }

  function remove(i: number) {
    const next = faqs.filter((_, idx) => idx !== i);
    setFaqs(next);
    void save(next);
  }

  function addPair() {
    setFaqs([...faqs, { question: '', answer: '' }]);
  }

  return (
    <section className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-brand-ink">FAQ pairs</h2>
        <button
          onClick={addPair}
          className="text-xs text-brand-green hover:text-brand-ink transition-colors"
        >
          + Add question
        </button>
      </div>
      <div className="space-y-4">
        {faqs.map((faq, i) => (
          <div key={i} className="border border-gray-100 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <input
                type="text"
                value={faq.question}
                onChange={(e) => update(i, 'question', e.target.value)}
                placeholder="Question"
                className="flex-1 text-sm font-medium border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green"
              />
              <button onClick={() => remove(i)} className="text-gray-400 hover:text-red-500 text-xs mt-1">✕</button>
            </div>
            <textarea
              value={faq.answer}
              onChange={(e) => update(i, 'answer', e.target.value)}
              placeholder="Answer"
              rows={2}
              className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-green"
            />
          </div>
        ))}
        {faqs.length === 0 && (
          <p className="text-sm text-gray-400 italic">No FAQ pairs yet. Click "+ Add question" above.</p>
        )}
      </div>
      {faqs.length > 0 && (
        <button
          onClick={() => save(faqs)}
          disabled={saving}
          className="mt-4 text-xs bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save FAQ changes'}
        </button>
      )}
      {toast && <p className="text-xs text-brand-green mt-2">{toast}</p>}
    </section>
  );
}
